import { marked } from "marked";

// GFM (tables, strikethrough, task lists, autolinks) is on by default in
// marked v5+. Keep parse synchronous and escape-safe (marked escapes HTML
// by default, which is what we want for untrusted markdown content).
marked.setOptions({ gfm: true, breaks: false });

// Preview styles mirroring the old .markdown-content rules in App.css.
// Scoped under .markdown-content so AI-injected <style> blocks can still
// override per-element styles when the user asks for "make text smaller" etc.
const MARKDOWN_CSS = `
.markdown-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  line-height: 1.6;
  color: #1f2328;
  padding: 20px;
  max-width: 980px;
  margin: 0 auto;
}
.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
}
.markdown-content h1 {
  font-size: 2em;
  border-bottom: 2px solid #eaecef;
  padding-bottom: 0.3em;
}
.markdown-content h2 {
  font-size: 1.5em;
  border-bottom: 1px solid #eaecef;
  padding-bottom: 0.3em;
}
.markdown-content p { margin-bottom: 1em; }
.markdown-content ul,
.markdown-content ol { margin-bottom: 1em; padding-left: 2em; }
.markdown-content li { margin-bottom: 0.25em; }
.markdown-content blockquote {
  margin: 1em 0;
  padding: 0 1em;
  border-left: 4px solid #dfe2e5;
  color: #6a737d;
}
.markdown-content code {
  background: rgba(175,184,193,0.2);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Fira Mono', 'Monaco', 'Cascadia Code', 'Segoe UI Mono', monospace;
  font-size: 0.9em;
}
.markdown-content pre {
  background: #f6f8fa;
  padding: 1em;
  border-radius: 5px;
  overflow-x: auto;
  margin: 1em 0;
}
.markdown-content pre code { background: none; padding: 0; }
.markdown-content a { color: #0366d6; text-decoration: none; }
.markdown-content a:hover { text-decoration: underline; }
.markdown-content hr { border: none; border-top: 2px solid #eaecef; margin: 2em 0; }
.markdown-content table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}
.markdown-content th,
.markdown-content td {
  border: 1px solid #dfe2e5;
  padding: 0.5em;
  text-align: left;
}
.markdown-content th { background: #f6f8fa; font-weight: 600; }
.markdown-content img { max-width: 100%; }

[data-theme="dark"] { background: #1e1e1e; color: #e4e4e4; }
[data-theme="dark"] .markdown-content { color: #e4e4e4; }
[data-theme="dark"] .markdown-content h1 { border-bottom-color: #404040; }
[data-theme="dark"] .markdown-content h2 { border-bottom-color: #404040; }
[data-theme="dark"] .markdown-content blockquote { border-left-color: #555; color: #aaa; }
[data-theme="dark"] .markdown-content code { background: #2d2d2d; color: #f8f8f2; }
[data-theme="dark"] .markdown-content pre { background: #2d2d2d; }
[data-theme="dark"] .markdown-content a { color: #4dabf7; }
[data-theme="dark"] .markdown-content hr { border-top-color: #444; }
[data-theme="dark"] .markdown-content th,
[data-theme="dark"] .markdown-content td { border-color: #555; }
[data-theme="dark"] .markdown-content th { background: #2d2d2d; }
`;

/**
 * Convert Markdown source into a standalone HTML document suitable for the
 * sandboxed preview iframe (and for download/print). The document body wraps
 * the rendered HTML in a `.markdown-content` container so the default styles
 * apply, while still allowing AI-injected <style> blocks / inline styles to
 * override (so "make the text smaller" works).
 */
export function buildMarkdownDocument(md, { dark = false } = {}) {
  const body = marked.parse(typeof md === "string" ? md : "");
  return `<!DOCTYPE html>
<html lang="ja" data-theme="${dark ? "dark" : "light"}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Markdown Preview</title>
  <style>
${MARKDOWN_CSS}
  </style>
</head>
<body>
  <div class="markdown-content">
${body}
  </div>
</body>
</html>`;
}
